import { buildStructuredListingTitle } from 'components/listing/buildStructuredListingTitle'
import { formatListingAvailabilityLabel } from 'components/propertyCard/formatListingAvailability'
import { formatListingPrice } from 'components/propertyCard/formatListingPrice'
import { Unit } from 'newTypes/unit'
import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import {
  canSubmitListingVerification,
  formatListingAddress,
  getListingStatusKey,
  isListingAwaitingVerification,
  listingStatusBadgeClass,
} from './mySpaceUtils'

type OwnerDashboardProps = {
  firstName: string
  listings: Unit[] | undefined
  isLoadingListings: boolean
  favoritesCount: number
  applicationsCount: number
  visitsCount: number
  statsLoading?: boolean
  pendingVisitCount?: number
  firstPendingVisitListingId?: string | null
  onOpenVerify: (listingId: string) => void
  onScrollToFavorites: () => void
  onScrollToApplications: () => void
  favoritesSlot?: ReactNode
  applicationsSlot?: ReactNode
}

function DashboardStat({
  icon,
  value,
  label,
  isLoading,
  onClick,
}: {
  icon: ReactNode
  value: number
  label: string
  isLoading?: boolean
  onClick?: () => void
}) {
  const className =
    'flex flex-col items-start rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:shadow-md ' +
    (onClick
      ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary'
      : '')

  const content = (
    <>
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-imovPrimary/10 text-imovPrimary">
        {icon}
      </div>
      <p className="text-2xl font-bold tabular-nums text-imovSecondary">
        {isLoading ? '—' : value}
      </p>
      <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-500">
        {label}
      </p>
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className} aria-label={label}>
        {content}
      </button>
    )
  }

  return <div className={className}>{content}</div>
}

const OwnerDashboard = ({
  firstName,
  listings,
  isLoadingListings,
  favoritesCount,
  applicationsCount,
  visitsCount,
  statsLoading,
  pendingVisitCount = 0,
  firstPendingVisitListingId = null,
  onOpenVerify,
  onScrollToFavorites,
  onScrollToApplications,
  favoritesSlot,
  applicationsSlot,
}: OwnerDashboardProps) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const items = listings ?? []

  const listingNeedingVerify = items.find(
    listing =>
      getListingStatusKey(listing) !== 'active' &&
      canSubmitListingVerification(listing.verified)
  )
  const listingMissingPhotos = items.find(listing => !listing.images?.length)

  const actionRequired = useMemo(() => {
    if (listingNeedingVerify) {
      return {
        title: t('account.mySpace.ownerDash.actionVerifyTitle'),
        body: t('account.mySpace.ownerDash.actionVerifyBody'),
        primaryLabel: t('account.mySpace.startSecurityVerification'),
        onPrimary: () => onOpenVerify(listingNeedingVerify.id),
        secondaryLabel: t('account.mySpace.ownerDash.later'),
        onSecondary: () => navigate(`/my-listings/${listingNeedingVerify.id}/manage`),
      }
    }
    if (listingMissingPhotos) {
      return {
        title: t('account.mySpace.ownerDash.actionPhotosTitle'),
        body: t('account.mySpace.ownerDash.actionPhotosBody'),
        primaryLabel: t('account.mySpace.ownerDash.addPhotos'),
        onPrimary: () =>
          navigate('/create-ad', {
            state: {
              editPropertyId: listingMissingPhotos.id,
              returnTo: '/account',
            },
          }),
        secondaryLabel: t('account.mySpace.ownerDash.later'),
        onSecondary: () => navigate(`/my-listings/${listingMissingPhotos.id}/manage`),
      }
    }
    if (pendingVisitCount > 0) {
      return {
        title: t('account.mySpace.ownerDash.actionVisitTitle'),
        body: t('account.mySpace.ownerDash.actionVisitBody', {
          count: pendingVisitCount,
        }),
        primaryLabel: t('account.mySpace.ownerDash.seeProposal'),
        onPrimary: () =>
          navigate(
            firstPendingVisitListingId
              ? `/my-listings/${firstPendingVisitListingId}/manage?tab=visits`
              : '/account#my-space-listings'
          ),
        secondaryLabel: t('account.mySpace.ownerDash.later'),
        onSecondary: undefined,
      }
    }
    return null
  }, [
    listingNeedingVerify,
    listingMissingPhotos,
    pendingVisitCount,
    firstPendingVisitListingId,
    navigate,
    onOpenVerify,
    t,
  ])

  const greetingName = firstName.trim() || t('account.mySpace.ownerDash.fallbackName')

  return (
    <div className="flex flex-col gap-6">
      <header className="min-w-0">
        <h1 className="break-words text-2xl font-bold text-imovSecondary md:text-3xl">
          {t('account.mySpace.ownerDash.greeting', { name: greetingName })}
        </h1>
        <p className="mt-1 max-w-xl text-sm text-gray-500 md:text-base">
          {t('account.mySpace.ownerDash.subtitle')}
        </p>
      </header>

      {actionRequired ? (
        <div className="overflow-hidden rounded-2xl border border-amber-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-l-4 border-amber-400 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                {t('account.mySpace.ownerDash.actionRequired')}
              </p>
              <p className="mt-1 font-semibold text-imovSecondary [overflow-wrap:anywhere]">
                {actionRequired.title}
              </p>
              <p className="mt-1 text-sm text-gray-600 [overflow-wrap:anywhere]">
                {actionRequired.body}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {actionRequired.onSecondary ? (
                <button
                  type="button"
                  onClick={actionRequired.onSecondary}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-imovSecondary transition hover:bg-gray-50"
                >
                  {actionRequired.secondaryLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={actionRequired.onPrimary}
                className="rounded-full bg-imovPrimary px-4 py-2 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
              >
                {actionRequired.primaryLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
        <section id="my-space-listings" className="min-w-0 scroll-mt-24">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-bold text-imovSecondary">
              {t('account.mySpace.listingsTitle')}
            </h2>
            <Link
              to="/create-ad"
              state={{ newListing: true }}
              className="text-sm font-semibold text-imovPrimary hover:text-imovPrimaryDark"
            >
              + {t('account.mySpace.newListing')}
            </Link>
          </div>

          {isLoadingListings ? (
            <div className="space-y-3">
              {[0, 1].map(i => (
                <div
                  key={i}
                  className="h-28 animate-pulse rounded-2xl border border-gray-100 bg-white"
                />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-12 text-center shadow-sm">
              <p className="text-sm text-gray-600">{t('account.mySpace.noListings')}</p>
              <Link
                to="/create-ad"
                state={{ newListing: true }}
                className="mt-4 inline-flex rounded-full bg-imovPrimary px-5 py-2.5 text-sm font-semibold text-white hover:bg-imovPrimaryDark"
              >
                {t('account.mySpace.ownerDash.publishCta')}
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {items.map(listing => {
                const statusKey = getListingStatusKey(listing)
                const thumb = listing.images?.[0]
                const title = buildStructuredListingTitle(t, listing)
                const address = formatListingAddress(listing)
                const price = formatListingPrice(listing.price, listing.currency)
                const availability = formatListingAvailabilityLabel(
                  t,
                  listing.availability,
                  i18n.language || 'fr-CA'
                )
                const showVerify =
                  statusKey !== 'active' &&
                  canSubmitListingVerification(listing.verified)
                const awaiting =
                  statusKey !== 'active' &&
                  isListingAwaitingVerification(listing.verified)

                return (
                  <li key={listing.id}>
                    <article className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition hover:shadow-md">
                      <button
                        type="button"
                        onClick={() => navigate(`/my-listings/${listing.id}/manage`)}
                        className="flex w-full flex-col gap-0 text-left sm:flex-row"
                      >
                        <div className="relative h-40 w-full shrink-0 overflow-hidden bg-gray-100 sm:h-auto sm:w-36 md:w-44">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt=""
                              className="h-full w-full object-cover sm:absolute sm:inset-0"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full min-h-[8rem] items-center justify-center text-xs text-gray-400">
                              —
                            </div>
                          )}
                          <span className="absolute left-2 top-2 rounded-md bg-white/95 px-2 py-1 text-[11px] font-bold text-imovSecondary shadow-sm">
                            {price}
                          </span>
                        </div>

                        <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <h3 className="truncate text-base font-bold text-imovSecondary">
                                {title}
                              </h3>
                              {address ? (
                                <p className="mt-1 flex items-center gap-1 truncate text-sm text-gray-500">
                                  <svg
                                    className="h-3.5 w-3.5 shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    aria-hidden
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                  </svg>
                                  {address}
                                </p>
                              ) : null}
                              <p className="mt-1 text-xs text-gray-500">{availability}</p>
                            </div>
                            <span
                              className={`shrink-0 ${listingStatusBadgeClass(statusKey)}`}
                            >
                              {t(`account.mySpace.listingStatus.${statusKey}`)}
                            </span>
                          </div>

                          <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3">
                            <p className="text-xs text-gray-400">
                              {awaiting
                                ? t('account.mySpace.awaitingVerification')
                                : '\u00a0'}
                            </p>
                            <span className="text-sm font-semibold text-imovPrimary">
                              {t('account.mySpace.ownerDash.viewTracking')} →
                            </span>
                          </div>
                        </div>
                      </button>

                      {showVerify ? (
                        <div className="border-t border-gray-100 px-4 py-3">
                          <button
                            type="button"
                            onClick={() => onOpenVerify(listing.id)}
                            className="w-full rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-600 sm:w-auto"
                          >
                            {t('account.mySpace.startSecurityVerification')}
                          </button>
                        </div>
                      ) : null}
                    </article>
                  </li>
                )
              })}
            </ul>
          )}

          {applicationsSlot ? <div className="mt-6">{applicationsSlot}</div> : null}
          {favoritesSlot ? <div className="mt-6">{favoritesSlot}</div> : null}
        </section>

        <aside className="flex flex-col gap-4 lg:sticky lg:top-24">
          <div className="grid grid-cols-2 gap-3">
            <DashboardStat
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              }
              value={items.length}
              label={t('account.mySpace.ownerDash.statListings')}
              isLoading={isLoadingListings}
            />
            <DashboardStat
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              value={applicationsCount}
              label={t('account.mySpace.ownerDash.statApplications')}
              isLoading={statsLoading}
              onClick={onScrollToApplications}
            />
            <DashboardStat
              icon={
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              }
              value={favoritesCount}
              label={t('account.mySpace.ownerDash.statFavorites')}
              isLoading={statsLoading}
              onClick={onScrollToFavorites}
            />
            <DashboardStat
              icon={
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              value={visitsCount}
              label={t('account.mySpace.ownerDash.statVisits')}
              isLoading={statsLoading}
            />
          </div>

          <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm">
            <span className="inline-flex rounded-md bg-imovPrimary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
              {t('account.mySpace.ownerDash.promoBadge')}
            </span>
            <div className="mt-3 flex items-start gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-imovPrimary/15 text-imovPrimary">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-imovSecondary">
                  {t('account.mySpace.ownerDash.promoTitle')}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-gray-600">
                  {t('account.mySpace.ownerDash.promoBody')}
                </p>
              </div>
            </div>
            <ul className="mt-3 space-y-1.5 text-sm text-gray-700">
              {(
                [
                  'promoPoint1',
                  'promoPoint2',
                  'promoPoint3',
                ] as const
              ).map(key => (
                <li key={key} className="flex items-start gap-2">
                  <span className="mt-0.5 text-imovPrimary" aria-hidden>
                    ✓
                  </span>
                  <span>{t(`account.mySpace.ownerDash.${key}`)}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/profile"
              className="mt-4 flex w-full items-center justify-center rounded-full bg-imovPrimary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-imovPrimaryDark"
            >
              {t('account.mySpace.ownerDash.promoCta')}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default OwnerDashboard
